import React from 'react';
import { router } from '@inertiajs/react';
import SignupLayout from '../Layout.jsx';
import PaymentForm, { FreeOrderConfirm } from '../../../components/PaymentForm.jsx';
import PromoCodeField from '../../../components/PromoCodeField.jsx';
import LiveCohortPolicy from '../../../components/LiveCohortPolicy.jsx';
import { formatPrice, ROLE_LABELS, TRAINING_LABELS } from '../../../constants/reviewVariants.js';
import { primaryBtnClass } from '../shared.jsx';
import { getCsrfToken as csrfToken } from '../../../lib/csrf.js';

const LIVE_COHORT_TRAINING_TYPE = 'recertification_20hr_live';

// Patches the BuyDraft and reloads the page so the server re-prepares the
// PaymentIntent/Subscription with the new promo state. The prepared-checkout
// cache is keyed on a hash that includes promo_code, so the change always
// triggers a fresh Stripe call.
async function syncPromoAndReload(promoCode) {
  await fetch('/api/buy_draft/merge', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify({ patch: { individual: { promo_code: promoCode } } }),
  });
  router.reload();
}

export default function IndividualCheckout({
  client_secret, publishable_key, return_url, mode, amount_cents,
  subtotal_cents, volume_discount_cents, promo_discount_cents, applied_promo,
  deal_discount_cents, applied_deal,
  role, training_type, plan, progress,
}) {
  const hasBreakdown = subtotal_cents != null;
  const showVolume = (volume_discount_cents || 0) > 0;
  const showPromo  = (promo_discount_cents || 0) > 0;
  const showDeal   = (deal_discount_cents || 0) > 0 && applied_deal;

  return (
    <SignupLayout progress={progress}>
      <div className="space-y-5">
        <div>
          <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">Complete your purchase</h2>
          <p className="text-sm text-gray-600">Securely complete payment below. You won't leave this page.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800">Order summary</h3>
                <button
                  type="button"
                  onClick={() => router.visit('/signup/individual/review')}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer"
                >
                  Edit
                </button>
              </div>
              <div className="text-sm space-y-1">
                <p className="font-medium text-gray-900">{ROLE_LABELS[role]}</p>
                <p className="text-xs text-gray-500">{TRAINING_LABELS[training_type]}</p>
              </div>

              {hasBreakdown && (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5 text-sm">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal</span><span>{formatPrice(subtotal_cents)}</span>
                  </div>
                  {showVolume && (
                    <div className="flex justify-between font-medium text-green-700">
                      <span>Volume discount</span><span>−{formatPrice(volume_discount_cents)}</span>
                    </div>
                  )}
                  {showDeal && (
                    <div className="flex justify-between font-medium text-green-700">
                      <span>{applied_deal.name}</span><span>−{formatPrice(deal_discount_cents)}</span>
                    </div>
                  )}
                  {showPromo && applied_promo && (
                    <div className="flex justify-between font-medium text-green-700">
                      <span>Promo ({applied_promo.code})</span><span>−{formatPrice(promo_discount_cents)}</span>
                    </div>
                  )}
                  {/* A deal locks one total, so a promo on top would bill below
                      the agreed figure; the field is hidden rather than left to
                      accept a code the server will ignore. */}
                  {!showDeal && (
                    <div className="pt-1">
                      <PromoCodeField
                        appliedCode={applied_promo?.code}
                        appliedLabel={applied_promo?.label}
                        onApplied={(code) => syncPromoAndReload(code)}
                        onRemoved={() => syncPromoAndReload(null)}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-900">{mode === 'subscription' ? 'Due today' : 'Total'}</span>
                <span className="text-lg font-bold text-indigo-600">{formatPrice(amount_cents)}</span>
              </div>

              {/* A deal discounts the first payment only, so a subscription
                  buyer renews at list. They are told before paying, in the same
                  words the standalone offer page uses. */}
              {showDeal && mode === 'subscription' && (
                <p className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                  This offer applies to your first payment. The subscription then renews at the
                  standard price of {formatPrice(subtotal_cents)} unless cancelled.
                </p>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              {client_secret ? (
                <PaymentForm
                  clientSecret={client_secret}
                  publishableKey={publishable_key}
                  returnUrl={return_url}
                  submitLabel={`Pay ${formatPrice(amount_cents)}`}
                />
              ) : (
                <FreeOrderConfirm returnUrl={return_url} reason={showDeal ? 'deal' : 'promo'} />
              )}
            </div>
            {training_type === LIVE_COHORT_TRAINING_TYPE && (
              <div className="mt-4">
                <LiveCohortPolicy />
              </div>
            )}
          </div>
        </div>
      </div>
    </SignupLayout>
  );
}
